import {
    useDrugProgram,
    useMedicationConfigOverride,
} from '../../../../contexts/DrugProgramProvider';
import type { MedicationConfigOverride } from '../../../../hooks/use-config-api';
import type {
    DrugProgram,
    NdcSwitchConfig,
} from '../../../../types/drug-config';
import { getNdcDefinitionByNdc } from '../../../../util/config/get-ndc-definition';

export const getSwitchTarget = (
    ndc: string | undefined,
    config: NdcSwitchConfig
) => {
    if (!ndc) {
        return undefined;
    }

    return config.targets.find((target) => target.ndc === ndc);
};

export const getSwitchConfigFromDrugProgram = (
    ndc: string,
    drugProgram: DrugProgram
): NdcSwitchConfig | null => {
    const ndcDefinition = drugProgram.drugConfigs
        .flatMap((config) => config.ndcDefinitions)
        .find((definition) => definition.ndc === ndc);

    return ndcDefinition?.switchConfig ?? null;
};

export const getSwitchConfigFromOverrides = (
    ndc: string,
    overrides: MedicationConfigOverride
): NdcSwitchConfig | null => {
    return overrides.ndcSwitchConfigs?.[ndc] ?? null;
};

export const useDrugSwitchConfig = (ndc?: string): NdcSwitchConfig | null => {
    const drugProgram = useDrugProgram();
    const configOverride = useMedicationConfigOverride();

    if (!ndc) {
        return null;
    }

    // NDC must exist in the drug program; its alternateConfigs drives which targets are allowed
    const ndcDefinition = getNdcDefinitionByNdc(drugProgram, ndc);
    if (!ndcDefinition) {
        return null;
    }

    const allowedNdcs = new Set(
        ndcDefinition.alternateConfigs.map((ac) => ac.alternateNdc)
    );

    // Overrides take precedence over drug program config
    const fullConfig =
        getSwitchConfigFromOverrides(ndc, configOverride) ??
        getSwitchConfigFromDrugProgram(ndc, drugProgram);

    if (!fullConfig) {
        return null;
    }

    // Only include targets that are explicitly configured in alternateConfigs
    const filteredTargets = fullConfig.targets.filter((target) =>
        allowedNdcs.has(target.ndc)
    );

    return { ...fullConfig, targets: filteredTargets };
};
